import { z } from 'zod';
import moment from 'moment';
import { criteriaConditionSchema } from 'modules/jsonRulesCriterias/businessCriteria';
import {
  FinanceProgramRateType,
  FinanceProgramType,
  MarkupType,
  PaymentPeriodTiming,
  SubsidyCapType,
} from '@trnsact/trnsact-shared-types';
import { FinanceProgramFormSteps, MarkupMethods, Structures } from './types';

export const financeProgramSchema = z.object({
  step: z.nativeEnum(FinanceProgramFormSteps),
  program: z.object({
    id: z.string().nullable(),
    first: z.object({
      name: z.string().min(1, { message: 'Required field' }),
      displayName: z.string().min(1, { message: 'Required field' }),
      guidelines: z.any(),
      lenders: z.object({ label: z.string(), value: z.string() }).array(),
      dealers: z.object({ label: z.string(), value: z.string() }).array(),
      notes: z.string(),
    }),
    second: z
      .object({
        rateType: z.nativeEnum(FinanceProgramRateType),
        terms: z.array(z.string()).refine(arr => arr.length > 0, {
          message: 'Please, select at least one term',
        }),
        rate: z.number({ message: 'Rate should be equal or greater than 0' }).gte(0, {
          message: 'Rate should be equal or greater than 0',
        }),
        minimumRequiredDownPayment: z.number({ message: 'Down Payment should be equal or greater than 0' }),
        specifyRates: z
          .object({
            term: z.string(),
            rate: z.number({ message: 'Rate should be equal or greater than 0' }).gte(0, {
              message: 'Rate should be equal or greater than 0',
            }),
          })
          .array(),
        customerRate: z.number().gte(0, {
          message: 'Rate should be equal or greater than 0',
        }),
        isSubsidized: z.boolean(),
        isSpecifyRate: z.boolean(),
        structure: z.nativeEnum(Structures),
        method: z.nativeEnum(MarkupMethods),
        dealerMarkup: z.number({ message: 'Markup should be equal or greater than 0' }).gte(0),
        programType: z.nativeEnum(FinanceProgramType),
        advancePayment: z.string().min(1, { message: 'Required field' }),
        daysToFirstPayment: z.string().min(0).optional().nullable(),
        paymentCalculationMethod: z.nativeEnum(PaymentPeriodTiming),
        residual: z.number({ message: 'Residual should be equal or greater than 0' }),
        isValidityDates: z.boolean(),
        startDate: z.any(),
        endDate: z.any(),
        fee: z
          .object({
            amount: z.number({ message: 'Amount should be equal or greater than 0' }).gte(0, {
              message: 'Amount should be equal or greater than 0',
            }),
            description: z.string(),
            isFinanced: z.boolean(),
            type: z.nativeEnum(MarkupType),
          })
          .array(),
        configureSubsidyCap: z.boolean(),
        commonLenderMarketRate: z.number().optional().nullable(),
        specifyLenderMarketRates: z.object({ rate: z.number(), term: z.number() }).array(),
        subsidyCapConfiguration: z
          .object({
            subsidyCapType: z.nativeEnum(SubsidyCapType).optional().nullable(),
            lenderMarketRates: z.object({ rate: z.number(), term: z.number() }).array().optional().nullable(),
            lenderName: z.string().optional().nullable(),
            maxSubsidyAmount: z.number().optional().nullable(),
            netInvoicePercentage: z.number().optional().nullable(),
          })
          .optional()
          .nullable(),
      })
      .refine(
        data => {
          if (data.isValidityDates) return !!data.startDate && !!data.endDate;
          return true;
        },
        {
          path: ['startDate', 'endDate'],
          message: 'Required',
        }
      )
      .superRefine((val, ctx) => {
        if (
          val.configureSubsidyCap &&
          val.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount &&
          (val.subsidyCapConfiguration.maxSubsidyAmount === null ||
            val.subsidyCapConfiguration.maxSubsidyAmount === undefined)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxSubsidyAmount'],
            message: 'Max Subsidy Amount is required when Subsidy Cap Type is FixedAmount',
          });
        }

        if (
          val.configureSubsidyCap &&
          val.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage &&
          (val.subsidyCapConfiguration.netInvoicePercentage === null ||
            val.subsidyCapConfiguration.netInvoicePercentage === undefined)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['netInvoicePercentage'],
            message: 'Net Invoice Percentage is required when Subsidy Cap Type is NetInvoicePercentage',
          });
        }
      }),
    third: z.object({
      criteria: z.object({ label: z.string(), value: z.string() }).nullable(),
      conditions: criteriaConditionSchema.array(),
    }),
    fourth: z.object({
      submissionSettings: z.object({ excludedDocuments: z.any({}).array() }),
    }),
    fifth: z.object({
      insuranceCoverage: z.object({
        coverageRequirements: z.any(),
        certificateRequirements: z.any(),
        otherInstructions: z.any(),
      }),
    }),
    sixth: z.object({}),
  }),
});

export type FinanceProgramForm = z.infer<typeof financeProgramSchema>;

export const initialFormValue: FinanceProgramForm = {
  step: FinanceProgramFormSteps.First,
  program: {
    id: null,
    first: {
      name: '',
      notes: '',
      lenders: [],
      dealers: [],
      guidelines: '',
      displayName: '',
    },
    second: {
      fee: [],
      rate: 0,
      terms: [],
      residual: 0,
      customerRate: 0,
      dealerMarkup: 0,
      specifyRates: [],
      daysToFirstPayment: '30',
      advancePayment: '0',
      isSpecifyRate: false,
      isValidityDates: false,
      isSubsidized: false,
      structure: Structures.None,
      minimumRequiredDownPayment: 0,
      programType: FinanceProgramType.Loan,
      rateType: FinanceProgramRateType.InterestRate,
      method: MarkupMethods.CustomerRate,
      endDate: moment().toDate(),
      startDate: moment().toDate(),
      paymentCalculationMethod: PaymentPeriodTiming.BeginningOfPeriod,
      configureSubsidyCap: false,
      specifyLenderMarketRates: [],
      subsidyCapConfiguration: {
        subsidyCapType: SubsidyCapType.NetInvoicePercentage,
        lenderMarketRates: [],
        lenderName: '',
        maxSubsidyAmount: 0,
        netInvoicePercentage: 0,
      },
    },
    third: {
      criteria: null,
      conditions: [],
    },
    fourth: {
      submissionSettings: {
        excludedDocuments: [],
      },
    },
    fifth: {
      insuranceCoverage: {
        coverageRequirements: '',
        certificateRequirements: '',
        otherInstructions: '',
      },
    },
    sixth: {},
  },
};
